import React, { useEffect, useMemo, useRef, useState } from 'react';
import Bugsnag from '@bugsnag/browser';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import useAssistant from 'hooks/useAssistant';
import { useAsyncMemo } from 'services/TypeUtils';
import AIUtils from 'services/AIUtils';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import Icon from 'components/Utils/NewIcon';
import { IconButton } from '@material-ui/core';
import DefaultChip from 'components/Utils/DefaultChip';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { TranscriptionChat } from 'components/Video/Jitsi/JitsiMeet';
import { StampPicker } from './AppointmentNoteAttachment';
import { UncontrolledCollapse } from '../../../Utils/Collapse';
import { GetPhysician } from '../../../../careand-redux/selectors';
import {
  Patient,
  Appointment,
  AppointmentNoteStamp,
  AppointmentTranscription,
  Physician,
} from 'generated/api';
import { useIsLoading } from '../../../../careand-redux/slices/ui/hooks';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import CalendarUtils from '../../../../services/CalendarUtils';
import moment from 'moment-timezone';
import { runSavedPromptTyped } from '../../../../thunks/runSavedPromptTyped';

const DEFAULT_ARRAY: never[] = [];

interface MergeTranscriptionModalProps {
  note: string;
  appointment: Appointment;
  insertText: (
    text: string,
    insertAtEnd?: boolean,
    replaceText?: boolean,
    openExtractHR?: boolean,
  ) => void;
  onClose: () => void;
  patient: Patient;
}

const MergeTranscriptionModal: React.FC<MergeTranscriptionModalProps> = ({
  note,
  appointment,
  insertText,
  onClose,
  patient,
}) => {
  const dispatch = useAppDispatch();
  const regenerateButtonRef = useRef<HTMLButtonElement>(null);

  const { runSavedPromptTyped, streamingText, loadingReply } = useAssistant(appointment?.id);

  const physician = useAppSelector(GetPhysician);

  const [currentStampId, setCurrentStamp] = useState<string | undefined | null>(
    physician?.preferences?.default_soap_stamp,
  );
  const [stamps, setStamps] = useState<Partial<Record<string, AppointmentNoteStamp>>>({});
  const [templateSuggestion, setTemplateSuggestion] = useState<
    Partial<AppointmentNoteStamp> | undefined
  >(undefined);
  const [newNote, setNewNote] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');
  const [editableNote, setEditableNote] = useState(note);
  const [personalPreferences, setPersonalPreferences] = useState(
    physician?.preferences?.note_prompt || '',
  );

  const canSavePreferences = personalPreferences !== (physician?.preferences?.note_prompt || '');
  const savingPreferences = useIsLoading(api.Physicians.updatePhysician);

  const patientId = patient.id;
  const appointmentId = appointment?.id;

  const patientHealthRecords = useAppSelector((state) => state.healthRecords.byId[patient.id!]);
  const patientReminders = useAppSelector((state) => state.reminders[patient.id!]) ?? DEFAULT_ARRAY;

  const stampList = useAppSelector((state) => state.healthRecords.appointmentNoteStamps);
  const stamp =
    stamps[currentStampId!]?.content ?? stampList?.find((it) => it.id === currentStampId)?.content;

  const transcription = useAsyncMemo(async () => {
    if (appointment.has_transcription && appointment.patient_id !== AIUtils.getDummyPatientId()) {
      return dispatch(api.Appointments.getAppointmentTranscription({ id: appointmentId! }));
    }

    return AIUtils.getDummyTranscription(appointmentId!);
  }, [appointmentId]);

  const loadingNote = loadingReply || !transcription;

  useEffect(() => {
    setTimeout(() => {
      regenerateButtonRef.current?.click();
    }, 100);
  }, [stamp]);

  useEffect(() => {
    const getStamp = async (id: string) => {
      id = id ?? currentStampId;
      let stamp = stamps[id];
      if (!stamp) {
        stamp = await dispatch(api.HealthRecords.getAppointmentNoteStamp({ id }));
        setStamps((prev) => ({ ...prev, [id]: stamp }));
      }
      return stamp;
    };

    if (currentStampId) {
      getStamp(currentStampId);
    }
  }, [dispatch, currentStampId, stamps]);

  useEffect(() => {
    if (transcription && stampList) {
      runSavedPromptTyped(
        'Note Stamp Suggestion from Transcription',
        {
          // Only id + name are needed to pick the best-matching stamp. Sending
          // the full stamp objects (each with a large `content` template body),
          // pretty-printed, was ~60k input tokens PER call — the bulk of this
          // prompt's cost — for a one-line suggestion. Slim it to id + name.
          templates: JSON.stringify(stampList.map((s) => ({ id: s.id, name: s.name }))),
          transcription: AIUtils.convertTranscriptionToText(transcription, appointment),
        },
        { patientId, streamingKey: appointmentId + 'suggestion' },
      ).then((id) => {
        if (id) {
          setTemplateSuggestion(stampList.find((s) => s.id === id));
        }
      });
    }
  }, [dispatch, stampList, patientId, transcription]);

  const records = useMemo(() => {
    return AIUtils.getRecordsForSmartP(appointment, patientHealthRecords!, patientReminders);
  }, [appointment, patientHealthRecords, patientReminders]);

  useEffect(() => {
    if (transcription) {
      regenerate(
        transcription,
        note,
        stamp,
        extraInstructions,
        records,
        appointment,
        personalPreferences,
      );
    }
  }, [transcription]);

  async function regenerate(
    transcription: AppointmentTranscription | undefined,
    note: string,
    stamp: string | undefined,
    extraInstructions: string | undefined,
    records: string | undefined,
    appointment: Appointment,
    personalPreferences: string,
  ) {
    if (!transcription?.messages || transcription.messages.length === 0) {
      return;
    }

    let instructions = extraInstructions;
    if (personalPreferences) {
      instructions += `\n\nPersonal Note Preferences:\n${personalPreferences}`;
    }

    let appointmentDescription = `${AppointmentUtils.getAppointmentDescription(
      appointment,
    )}\n${AppointmentUtils.getConcernComplaintsText(appointment, true)}`;

    setNewNote('');
    let value = await runSavedPromptTyped(
      'Transcription to Note',
      {
        date: CalendarUtils.format(moment().unix(), 'Do MMM YYYY'),
        note: note || 'N/A',
        template: stamp || 'N/A',
        transcription: AIUtils.convertTranscriptionToText(transcription, appointment) || 'N/A',
        instructions: instructions || 'N/A',
        appointment: appointmentDescription || 'N/A',
        records: records || 'N/A',
      },
      { patientId },
    );
    if (value) {
      if (AppointmentUtils.isTelemedicineAppointment(appointment)) {
        value = `Telemedicine Appointment\n\n${value}`;
      }
      setNewNote(value);
    }
  }

  async function onSavePreferences() {
    let updated: Physician = {
      ...physician!,
      preferences: {
        ...(physician!.preferences || {}),
        note_prompt: personalPreferences,
      },
    };

    await dispatch(api.Physicians.updatePhysician({ id: physician!.id, physician: updated }));
  }

  return (
    <DefaultModal
      title="Generate SOAP Note"
      content={
        <div className="merge-transcription-modal grid grid-cols-3 h-full overflow-hidden">
          <div className="relative border-r border-gray-100 border-solid overflow-hidden flex flex-col">
            <div className="title bg-white z-10">Settings</div>
            <div className="options-section flex-grow">
              <div className="card gap-2">
                <div className="small-title">Stamp</div>
                <div className="stamp-wrapper flex items-center gap-2">
                  <StampPicker
                    label=" "
                    style={{ height: 'unset', flexGrow: '1' }}
                    currentStampId={currentStampId}
                    onStampChanged={(id: string) => {
                      setCurrentStamp(id);
                    }}
                    disabled={loadingNote}
                  />
                  <Icon
                    src="circle-question"
                    alt={stamps[currentStampId!]?.content ?? 'Select a stamp to preview here'}
                    showTooltip
                    style={{ margin: 0 }}
                  />
                  {currentStampId && (
                    <IconButton disabled={loadingNote} onClick={() => setCurrentStamp(null)}>
                      <Icon
                        src="times-circle"
                        className="red"
                        alt="Clear selected stamp"
                        showTooltip={true}
                      />
                    </IconButton>
                  )}
                </div>
                {templateSuggestion && templateSuggestion.id !== currentStampId && (
                  <div className="suggestion">
                    <span>Carey's suggestion:</span>{' '}
                    <DefaultChip
                      disabled={loadingNote}
                      onClick={() => {
                        setCurrentStamp(templateSuggestion.id);
                        // Usefulness metric: clinician applied Carey's stamp suggestion.
                        dispatch(
                          api.Resource.recordAiSuggestionAccepted({
                            aiSuggestionAccepted: {
                              prompt: 'Note Stamp Suggestion from Transcription',
                              patient_id: patientId,
                              metadata: { stamp_id: templateSuggestion.id },
                            },
                          }),
                        );
                      }}
                      text={templateSuggestion.name}
                      backgroundColor="#47AFB7"
                      textColor="white"
                    />
                  </div>
                )}
              </div>
              <div className="card flex-grow">
                <div className="small-title">Notes</div>
                <textarea
                  placeholder="Enter private notes"
                  value={editableNote}
                  onChange={(ev) => setEditableNote(ev.target.value)}
                />
              </div>
              <UncontrolledCollapse title="Your Preferences">
                <div className="card" style={{ minHeight: '150px' }}>
                  <textarea
                    placeholder="Personal preferences for Carey"
                    value={personalPreferences}
                    onChange={(ev) => setPersonalPreferences(ev.target.value)}
                  />
                  <DefaultButton
                    name="Save Preferences"
                    className="mt-2 white"
                    disabled={!canSavePreferences}
                    loading={savingPreferences}
                    icon="floppy-disk"
                    style={{ width: '200px' }}
                    onClick={onSavePreferences}
                  />
                </div>
              </UncontrolledCollapse>
              <UncontrolledCollapse title="Extra Instructions">
                <div className="card" style={{ minHeight: '150px' }}>
                  <textarea
                    placeholder="Enter additional instructions for Carey"
                    value={extraInstructions}
                    onChange={(ev) => setExtraInstructions(ev.target.value)}
                  />
                </div>
              </UncontrolledCollapse>
            </div>
          </div>
          <div
            className="merge-section relative overflow-auto border-r border-gray-100 border-solid"
            style={{
              display: 'grid',
              overflow: 'hidden',
              gridTemplateRows: 'auto 1fr auto',
            }}
          >
            <div className="title bg-white z-10">Generated</div>
            <div className="card mt-[10px] overflow-auto">
              <div className="small-title">Generated</div>
              <textarea
                disabled={loadingNote}
                value={newNote || streamingText}
                onChange={(ev) => setNewNote(ev.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-center py-[10px]">
              <DefaultButton
                name="Regenerate"
                icon="arrows-rotate"
                loading={loadingNote}
                onClick={() =>
                  regenerate(
                    transcription,
                    editableNote,
                    stamp,
                    extraInstructions,
                    records,
                    appointment,
                    personalPreferences,
                  )
                }
                buttonRef={regenerateButtonRef}
              />
              <ConfirmationButton
                className="blue h-[40px]"
                name="Accept Note"
                icon="check"
                disabled={!newNote || loadingNote}
                confirmation={{
                  message:
                    'Do you want to extract the health records from this note automatically?',
                  confirmLabel: 'Yes, extract HR',
                  cancelLabel: 'No, just the note',
                  cancelClassName: 'white',
                  onCancel: () => {
                    insertText(newNote, false, true);
                    onClose();
                  },
                }}
                onClick={() => {
                  insertText(newNote, false, true, true);
                  onClose();
                }}
              />
            </div>
          </div>

          <div className="transcription-section relative h-full overflow-hidden flex flex-col">
            <div className="transcription-wrapper overflow-hidden flex-grow flex flex-col">
              {/*
            <div className="title bg-white z-10">AI Suggestions</div>
              <div className="p-[10px] overflow-scroll max-h-full shrink-0">
                <PrescriptionSuggestion appointment={appointment} note={note} />
              </div>
            */}
              <div className="ai-suggestions overflow-hidden flex flex-col">
                <div className="title bg-white z-10">Transcription</div>
                {transcription && (
                  <TranscriptionChat
                    transcription={transcription}
                    appointmentId={appointment.id}
                    enableAutoBackup={false}
                    enableManualSave={false}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      }
      isOpen
      onRequestClose={onClose}
      shouldCloseOnEsc={false}
      className="large-modal"
      wrap
      padding={false}
    />
  );
};

export default MergeTranscriptionModal;
